/**
 * PlatformPlaceIdCard — Iter 75.
 *
 * Small admin card (inside the Google Places section of the integrations
 * panel) for pasting the platform's OWN Google Business Profile Place ID.
 * Accepts a bare Place ID or a Maps URL containing `place_id=` — the
 * backend extracts it. Drives the "RentalCar.al on Google" homepage block.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Save, Building2, ExternalLink, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export const PlatformPlaceIdCard = () => {
  const [saved, setSaved] = useState("");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/site-settings").catch(() => null),
      api.get("/google-reviews/platform").catch(() => null),
    ]).then(([settings, platform]) => {
      if (cancelled) return;
      const pid = settings?.data?.platform_google_place_id || "";
      setSaved(pid);
      setValue(pid);
      setPreview(platform ? platform.data : null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/site-settings", { platform_google_place_id: value.trim() });
      const pid = data?.platform_google_place_id || "";
      setSaved(pid);
      setValue(pid);
      toast.success(pid ? "Platform Place ID saved" : "Platform Place ID cleared");
      try {
        const { data: p } = await api.get("/google-reviews/platform");
        setPreview(p);
      } catch { /* preview refresh is best-effort */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-24 rounded-2xl bg-neutral-100 animate-pulse mt-6" data-testid="platform-placeid-loading" />;

  const live = preview?.enabled && preview?.configured && preview?.rating;

  return (
    <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50/40 p-5" data-testid="platform-placeid-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center ring-1 ring-sky-200 shrink-0">
          <Building2 className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-sky-900">RentalCar.al's own Google Business Profile</h4>
          <p className="text-xs text-sky-800/80 mt-0.5 leading-relaxed">
            Paste your Google Maps <strong>share link</strong> (e.g.{" "}
            <span className="font-mono">maps.app.goo.gl/…</span> — from your listing tap Share → Copy link),
            a full Maps URL, or the raw <strong>Place ID</strong>. We resolve it automatically. This powers the
            "Official Google rating" block on the homepage. Prefer the raw ID? Use the{" "}
            <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
              Place ID Finder <ExternalLink className="h-3 w-3" />
            </a>.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-xs font-medium text-sky-900">Maps share link or Place ID</Label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://maps.app.goo.gl/… or ChIJ…"
            className="rounded-xl mt-1 bg-white"
            data-testid="platform-placeid-input"
          />
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={saving || value.trim() === saved}
          className="rounded-full bg-sky-600 hover:bg-sky-700"
          data-testid="platform-placeid-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
      <div className="mt-3 text-xs" data-testid="platform-placeid-status">
        {!saved ? (
          <span className="inline-flex items-center gap-1.5 text-sky-700/80">
            <AlertCircle className="h-3.5 w-3.5" /> Not configured — the homepage rating block stays hidden until a Place ID is saved and the Places API is enabled.
          </span>
        ) : live ? (
          <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
            <CheckCircle2 className="h-3.5 w-3.5" /> Live — {preview.display_name || "RentalCar.al"} · {Number(preview.rating).toFixed(1)}★ · {preview.total} reviews. Showing on the homepage.
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-amber-700">
            <AlertCircle className="h-3.5 w-3.5" /> Place ID saved, but no live rating yet — usually the Places API (New) isn't enabled for the key, or Google hasn't published a rating for this listing.
          </span>
        )}
      </div>
    </div>
  );
};

export default PlatformPlaceIdCard;
